import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings } from 'lucide-react';
import { Button } from './ui/button';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Toujours activé
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Vérifier si l'utilisateur a déjà fait un choix
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Attendre 1 seconde avant d'afficher la bannière
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Charger les préférences sauvegardées
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
      } catch (e) {
        console.error('Error loading cookie preferences:', e);
      }
    }
  }, []);

  const acceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    savePreferences(allAccepted);
  };

  const acceptEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    savePreferences(essentialOnly);
  };

  const saveCustomPreferences = () => {
    savePreferences(preferences);
  };

  const savePreferences = (prefs) => {
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setPreferences(prefs);
    setShowBanner(false);
    setShowSettings(false);
    
    // Ici, vous pouvez activer/désactiver les scripts analytics selon les préférences
    if (prefs.analytics) {
      // Activer Google Analytics ou autre
      console.log('Analytics enabled');
    }
    if (prefs.marketing) {
      // Activer les cookies marketing
      console.log('Marketing cookies enabled');
    }
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slideUp">
        <div className="max-w-6xl mx-auto bg-slate-800 rounded-2xl shadow-2xl border border-slate-700 p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            {/* Icon */}
            <div className="flex-shrink-0">
              <Cookie className="w-12 h-12 text-blue-400" />
            </div>

            {/* Content */}
            <div className="flex-1">
              <h3 className="text-white font-bold text-lg mb-2">
                🍪 Nous utilisons des cookies
              </h3>
              <p className="text-gray-300 text-sm">
                Nous utilisons des cookies pour améliorer votre expérience sur notre site. 
                Les cookies essentiels sont nécessaires au fonctionnement du site. 
                Vous pouvez accepter tous les cookies ou personnaliser vos préférences.
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <Button
                onClick={() => setShowSettings(true)}
                variant="outline"
                className="border-slate-600 text-gray-300 hover:bg-slate-700"
              >
                <Settings className="w-4 h-4 mr-2" />
                Personnaliser
              </Button>
              <Button
                onClick={acceptEssential}
                variant="outline"
                className="border-slate-600 text-gray-300 hover:bg-slate-700"
              >
                Essentiels uniquement
              </Button>
              <Button
                onClick={acceptAll}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Tout accepter
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-slate-800 rounded-2xl max-w-2xl w-full shadow-2xl border border-slate-700">
            {/* Header */}
            <div className="bg-slate-900 px-6 py-4 border-b border-slate-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Paramètres des cookies
              </h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Essential Cookies */}
              <div className="flex items-start gap-4">
                <input
                  type="checkbox"
                  checked={preferences.essential}
                  disabled
                  className="mt-1 w-5 h-5 rounded"
                />
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-1">
                    Cookies essentiels
                    <span className="ml-2 text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded">
                      Obligatoire
                    </span>
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Ces cookies sont nécessaires au fonctionnement du site et ne peuvent pas être désactivés. 
                    Ils permettent la navigation et l'utilisation des fonctionnalités de base.
                  </p>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start gap-4">
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                  className="mt-1 w-5 h-5 rounded"
                />
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-1">
                    Cookies analytiques
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Ces cookies nous aident à comprendre comment les visiteurs utilisent notre site. 
                    Les données sont anonymisées et utilisées uniquement pour améliorer nos services.
                  </p>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-start gap-4">
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                  className="mt-1 w-5 h-5 rounded"
                />
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-1">
                    Cookies marketing
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Ces cookies sont utilisés pour vous proposer du contenu personnalisé et des publicités pertinentes. 
                    Ils peuvent être définis par nos partenaires publicitaires.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-900 px-6 py-4 border-t border-slate-700 flex justify-end gap-3">
              <Button
                onClick={() => setShowSettings(false)}
                variant="outline"
                className="border-slate-600 text-gray-300"
              >
                Annuler
              </Button>
              <Button
                onClick={saveCustomPreferences}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Enregistrer mes préférences
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
